import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Appointment, SetWorkingHours, WorkingHours } from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS6 brief item 3 — per-staff WEEKLY working-hours grid (the rota base grid). This is the recurring
 * weekly band that drives the availability engine; the WS7 time-off / per-date hour-exception editors
 * (data/scheduling.ts) layer date-specific overrides on top of it. Mirrors the patterns in
 * data/staff.ts / data/scheduling.ts: TanStack Query over the generated client via unwrap().
 *
 * RBAC: the API's PUT /staff/:id/working-hours is manager-or-self (same predicate as the WS7 time-off
 * / hour-exception endpoints) — a chair_renter / stylist sets their OWN base grid, a manager edits
 * anyone's. The UI surfaces that exactly, so a non-manager editing their own grid never hits a save
 * 403. The list is cached under qk.workingHours(staffId) — the same key the diary's availability read
 * uses, so saving the grid refreshes availability for free.
 */

export function useWorkingHours(staffId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.workingHours(staffId ?? ''),
    enabled: Boolean(staffId) && enabled,
    queryFn: async (): Promise<WorkingHours[]> =>
      unwrap(await api.GET('/v1/staff/{id}/working-hours', { params: { path: { id: staffId! } } })),
  });
}

/**
 * Replace a staff member's whole weekly grid (the endpoint is a PUT — it deletes-then-inserts). The
 * caller hands the full set of blocks; we invalidate both this member's working-hours and any cached
 * appointments so the diary re-derives availability against the new band. The grid editor itself
 * computes which existing bookings now fall OUTSIDE the proposed hours and warns BEFORE this fires —
 * the save is non-destructive to bookings (they're never auto-cancelled).
 */
export function useSetWorkingHours(staffId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetWorkingHours): Promise<WorkingHours[]> =>
      unwrap(
        await api.PUT('/v1/staff/{id}/working-hours', {
          params: { path: { id: staffId } },
          body: body as never,
        }),
      ),
    onSuccess: (rows) => {
      qc.setQueryData(qk.workingHours(staffId), rows);
      // Availability is derived from the weekly band — drop diary appointment caches so a re-open
      // recomputes bookable gaps against the new hours.
      qc.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

/**
 * Forward window of a staff member's upcoming bookings, used by the grid to check whether a proposed
 * change to the weekly band would strand any existing appointment. We fetch one wide window (single
 * request, RLS-scoped, staff-filtered) rather than per-day — the diary's hot-path budget is for the
 * day view, not this background settings check. Defaults to the next ~8 weeks: long enough to catch a
 * standing weekly booking, short enough to stay cheap.
 *
 * Returns only booked/confirmed-style appointments (cancelled/no-show ones can't be "stranded").
 */
export function useUpcomingAppointments(
  staffId: string | undefined,
  fromIso: string,
  toIso: string,
) {
  return useQuery({
    queryKey: ['working-hours-conflicts', staffId ?? '', fromIso, toIso],
    enabled: Boolean(staffId),
    staleTime: 60_000,
    queryFn: async (): Promise<Appointment[]> => {
      const rows = unwrap(
        await api.GET('/v1/appointments', {
          params: { query: { from: fromIso, to: toIso, staffId: staffId! } },
        }),
      );
      return rows.filter((a) => a.status !== 'cancelled' && a.status !== 'no_show');
    },
  });
}
